import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket.tsx";
import { Button } from "@/components/core/buttons/button.tsx";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/core/drawer.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import {
	notificationsQueryOptions,
	unreadNotificationsQueryOptions,
	useReadNotificationMutation,
} from "@/features/dashboard/api/queries/notification-queries.ts";
import type { Notification as NotificationType } from "@/features/dashboard/types/notification.ts";
import { formatDate } from "@/utils/format-date.ts";
import { IconArrowRight, IconBell, IconCalendarCheck, IconGavel, IconMoodLookDown, IconSettings } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { formatDistanceToNowStrict } from "date-fns";
import { useEffect, useState } from "react";
import { ScrollArea } from "../core/scroller";

function NotificationItem({
	notification,
	onRead,
}: {
	notification: NotificationType;
	onRead: (id: string) => void;
}) {
	const isUnread = !notification.read_at;

	const formatDate = (dateString: string) => {
		return formatDistanceToNowStrict(new Date(dateString), { addSuffix: true });
	};

	return (
		<Link
			to={notification.data.attributes?.caseId ? `/case-evaluation/${notification.data.attributes?.caseId}?tabs=case-flow` : "/"}
			className="flex flex-row items-center w-full rounded-md gap-2 p-2 hover:bg-slate-50"
			onClick={() => onRead(notification.id)}
		>
			<div
				className={`relative self-start inline-flex justify-center items-center shrink-0 w-[32px] h-[32px] rounded-full
          ${isUnread ? "bg-blue-600" : "bg-slate-300"} text-white`}
			>
				{isUnread && (
					<div className="absolute -top-1 -right-1 self-start inline-flex shrink-0 w-[12px] h-[12px] p-[2px] mt-[4px] rounded-full bg-background">
						<div className="inline-flex w-full h-full rounded-full bg-green-600" />
					</div>
				)}
				{notification.data.attributes?.activity_type === "Hearing" ? (
					<IconCalendarCheck size={18} stroke={2} className="bg-teal-500 w-[32px] h-[32px] text-white p-1.5 rounded-full" />
				) : notification.category === "system" ? (
					<IconSettings size={18} stroke={1.5} />
				) : (
					<IconGavel size={18} stroke={1.5} />
				)}
			</div>
			<div className="flex flex-row items-start w-full gap-2">
				<div className="flex flex-col w-full">
					<div className="inline-flex flex-row items-center flex-wrap">
						<span className="text-sm font-bold text-foreground-dark">{notification.data.title}</span>
					</div>
					{notification?.data.message && (
						<div
							className="notification-message truncate max-w-[320px]"
							// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
							dangerouslySetInnerHTML={{
								__html: notification.data.message,
							}}
						/>
					)}
					<span className="text-sm text-foreground/50 whitespace-nowrap">{formatDate(notification.created_at)}</span>
				</div>
			</div>
		</Link>
	);
}

export function Notification() {
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [category, setCategory] = useState("");
	const unreadNotificationsQuery = useQuery(unreadNotificationsQueryOptions());
	const notificationQuery = useQuery(
		notificationsQueryOptions(
			{
				category,
			},
			{
				enabled: !!isDrawerOpen,
			},
		),
	);
	const readNotificationMutation = useReadNotificationMutation();
	const unreadNotifications = unreadNotificationsQuery.data;
	const notifications = notificationQuery.data;

	/*const onRead = (id: string, notification: any) => {
		readNotificationMutation.mutate(id, {
			onSuccess: () => {
				notificationQuery.refetch();
				if (notification && notification.data.url) {
					window.location.href = notification.data.url;
				}
			},
		});
	};*/

	const onRead = (id: string, notification?: Notification) => {
		readNotificationMutation.mutate(id, {
			onSuccess: () => {
				notificationQuery.refetch();

				const url = notification?.data?.url;
				if (url) {
					window.location.href = url;
				}
			},
		});
	};

	// useEffect(() => {
	//   if (isDrawerOpen) {
	//     readNotificationMutation.mutateAsync();
	//   }
	// }, [isDrawerOpen]);
	return (
		<div>
			<button
				onClick={() => setIsDrawerOpen(true)}
				type="button"
				className="relative inline-flex justify-center items-center w-[32px] h-[32px] rounded-md bg-primary-100 hover:bg-primary-200 text-primary-600"
			>
				{unreadNotifications?.unread_count !== 0 && (
					<div className="absolute -top-1.5 -right-1.5 inline-flex shrink-0 justify-center items-center min-h-[18px] min-w-[18px] px-1 rounded-full bg-red-600 border-[2px] border-primary-50 pointer-events-none">
						<span className="text-xs font-semibold text-white">
							{Number.parseInt(unreadNotifications?.unread_count) < 9 ? unreadNotifications?.unread_count : "9+"}
						</span>
					</div>
				)}
				<IconBell size={18} stroke={2} />
			</button>

			<Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
				<DrawerTrigger asChild>
					<div />
				</DrawerTrigger>
				<DrawerContent className="flex flex-col h-full" side="right">
					<DrawerHeader className="p-4">
						<DrawerTitle>
							<span className="text-xl text-foreground-dark font-semibold">Notifications</span>
						</DrawerTitle>
						<DrawerDescription>
							<span className="text-base text-foreground/70">Here are your recent notifications.</span>
						</DrawerDescription>
					</DrawerHeader>
					<div className="flex flex-col grow shrink w-full">
						<Tabs
							onValueChange={async (value) => {
								setCategory(value);
							}}
							defaultValue={category}
							variant="compact"
						>
							<TabsList>
								<TabsTrigger value="">All</TabsTrigger>
								<TabsTrigger value="compliance">Compliance</TabsTrigger>
								<TabsTrigger value="system">System</TabsTrigger>
							</TabsList>
							<TabsContent value={category}>
								<ScrollArea className="h-[calc(100svh-110px)]">
									{notifications?.data.length !== 0 ? (
										<div className="flex flex-col w-full p-2">
											{notifications?.data.slice(0, 20).map((notification) => (
												<NotificationItem key={notification.id} notification={notification} onRead={(value) => onRead(value, notification)} />
											))}
											<div className="flex flex-row w-full my-2">
												<Link
													to="/notifications"
													className="flex flex-row items-center justify-center w-full p-4 gap-2 rounded-md hover:bg-slate-50 text-blue-600 bg-blue-50"
												>
													<span className="text-base">See All Notifications</span>
													<IconArrowRight size={16} />
												</Link>
											</div>
										</div>
									) : (
										<div className="flex flex-col w-full p-4">
											<Blanket width="sm">
												<BlanketImage icon={<IconMoodLookDown size={30} stroke={1.5} />} />
												<BlanketInfo title="No Data" message="No available data for for now. Please check back later or refresh the page." />
											</Blanket>
										</div>
									)}
								</ScrollArea>
							</TabsContent>
						</Tabs>
					</div>
				</DrawerContent>
			</Drawer>
		</div>
	);
}
